import { MapPinLineIcon } from "@phosphor-icons/react";
import { useState } from "react";
import { useIntl } from "react-intl";

import { Button } from "@/components/Button";
import {
  DrawerTopBar,
  DrawerContent,
  DrawerFooter,
  SwipeableDrawer,
} from "@/components/Drawer";
import { TextField } from "@/components/TextField/TextField";
import {
  HotelSearchForm,
  type HotelSearchFormOnChangeSource,
} from "@/features/hotels/components/HotelSearchForm/HotelSearchForm";
import { useHotelDataContext } from "@/features/hotels/contexts/HotelDataContext";
import { useHotelSearchDataContext } from "@/features/hotels/contexts/HotelSearchDataContext";
import type { HotelSearchFormInputValues } from "@/features/hotels/utils/hotelsValidation";
import { getTotalVisitors } from "@/features/hotels/utils/rooms";
import { HotelTrackingEvents, rudderAnalytics } from "@/lib/rudderstack";
import { getDateString } from "@/utils/dates";

type HotelDetailsStickySearchDrawerProps = {
  open: boolean;
  onClose: () => void;
};

export function HotelDetailsStickySearchDrawer(
  props: HotelDetailsStickySearchDrawerProps,
) {
  const { open, onClose } = props;

  return (
    <SwipeableDrawer open={open} onClose={onClose} hasFullHeight>
      <HotelDetailsStickySearchDrawerContent {...props} />
    </SwipeableDrawer>
  );
}

function HotelDetailsStickySearchDrawerContent({
  onClose,
}: HotelDetailsStickySearchDrawerProps) {
  const intl = useIntl();

  const { data: hotel } = useHotelDataContext();
  const { searchData, updateSearchData } = useHotelSearchDataContext();
  const [localSearchData, setLocalSearchData] = useState(searchData);

  const handleSearchChange = () => {
    updateSearchData(localSearchData);
    onClose();
  };

  const handleLocalSearchChange = (
    values: HotelSearchFormInputValues,
    source: HotelSearchFormOnChangeSource,
  ) => {
    if (localSearchData) {
      const oldCheckIn = getDateString(localSearchData.dates.checkIn);
      const oldCheckOut = getDateString(localSearchData.dates.checkOut);
      const newCheckIn = getDateString(values.dates.checkIn);
      const newCheckOut = getDateString(values.dates.checkOut);

      if (source === "dates") {
        const isStartDateChanged = oldCheckIn !== newCheckIn;
        const isEndDateChanged = oldCheckOut !== newCheckOut;

        rudderAnalytics.track(
          HotelTrackingEvents.USER_EDIT_DATES_HOTEL_DETAILS_PAGE,
          {
            ...(isStartDateChanged && {
              old_start_date: oldCheckIn,
              new_start_date: newCheckIn,
            }),
            ...(isEndDateChanged && {
              old_end_date: oldCheckOut,
              new_end_date: newCheckOut,
            }),
            is_changed: isStartDateChanged || isEndDateChanged,
          },
        );
      }

      const oldGuestCount = getTotalVisitors(localSearchData.rooms);
      const oldRoomCount = localSearchData.rooms.length;
      const newGuestCount = getTotalVisitors(values.rooms);
      const newRoomCount = values.rooms.length;

      if (source === "rooms") {
        const isGuestCountChanged = oldGuestCount !== newGuestCount;
        const isRoomCountChanged = oldRoomCount !== newRoomCount;

        rudderAnalytics.track(
          HotelTrackingEvents.USER_EDIT_ROOMSPAX_HOTEL_DETAILS_PAGE,
          {
            ...(isGuestCountChanged && {
              old_guest_count: oldGuestCount,
              new_guest_count: newGuestCount,
            }),
            ...(isRoomCountChanged && {
              old_room_count: oldRoomCount,
              new_room_count: newRoomCount,
            }),
            is_changed: isGuestCountChanged || isRoomCountChanged,
          },
        );
      }
    }

    setLocalSearchData(values);
  };

  return (
    <>
      <DrawerTopBar
        onCloseClick={onClose}
        title={intl.formatMessage({
          defaultMessage: "Edit Search",
          description: "Title for the drawer to edit hotel search criteria",
        })}
      />

      <DrawerContent>
        <HotelSearchForm
          analyticsSource="sticky"
          initialValues={localSearchData}
          onChange={handleLocalSearchChange}
          slots={{
            destination: () => (
              <TextField
                disabled
                label={intl.formatMessage({
                  defaultMessage: "Hotel name",
                  description:
                    "Label for the hotel name field in the sticky search form on hotel details page",
                })}
                startAdornment={<MapPinLineIcon />}
                value={hotel?.name || "-"}
              />
            ),
          }}
        />
      </DrawerContent>

      <DrawerFooter>
        <Button fullWidth variant="contained" onClick={handleSearchChange}>
          {intl.formatMessage({
            defaultMessage: "Search hotel",
            description:
              "Label for the button that applies the changes made in the sticky hotel search form",
          })}
        </Button>
      </DrawerFooter>
    </>
  );
}
